import { defineStore } from 'pinia'
import { useIdentity } from '~/store/identity'
import getPaymentMethods from '~/utils/balance/getPaymentMethods'
import { useStoreConfig } from '~/store/storeConfig'
import type {
  CountryItem,
  FrontendPaymentProcessor,
  PaymentCardActionResponse,
  PaymentIntentResponse,
} from '~/types/payment/paymentFrontedTypes'
import { PaymentFeeTypes } from '~/enums/PaymentFeeTypes'
import PaymentProcessorsEnum from '~/enums/PaymentProcessorsEnum'
import toPrice from '~/utils/toPrice'
import loadStripe from '~/utils/loadStripe'
import confirmPaymentIntent from '~/utils/balance/confirmPaymentIntent'
import utilsExecutePayment from '~/utils/balance/executePayment'
import getAvailableCountries from '~/utils/balance/getAvailableCountries'
import type { DestinationInterface } from '~/types/destinationTypes'
import PaymentActionResponseEnum from '~/enums/payments/PaymentActionResponseEnum'
import { fetchRetryParameters } from '~/composables/constants/api'

interface balanceStateInterface {
  countries: Array<CountryItem>
  paymentMethods: FrontendPaymentProcessor[]
  selectedMethod: FrontendPaymentProcessor | null
  hasError: boolean
  errorMessage: string
  stripeEnabled: boolean | null
  stripe: any
}

export const useBalanceStore = defineStore('balance', {
  state: (): balanceStateInterface => {
    return {
      countries: [],
      paymentMethods: [],
      selectedMethod: null,
      hasError: false,
      errorMessage: '',
      stripeEnabled: null,
      stripe: {
        instance: null,
        card: null,
        token: null,
        isFirst: true,
      },
    }
  },
  getters: {
    stripeMethod(): FrontendPaymentProcessor | null {
      if (Array.isArray(this.paymentMethods)) {
        return (
          this.paymentMethods.find((method: FrontendPaymentProcessor) => {
            return method.type === PaymentProcessorsEnum.Stripe
          }) ?? null
        )
      }

      return null
    },
    paymentMethodOptions(): { label: string; value: string; attrs: any; data: FrontendPaymentProcessor }[] {
      return this.paymentMethods.map((item: FrontendPaymentProcessor) => {
        const value = item.key ? `${item.id}_${item.key}` : item.id.toString()

        return {
          label: value,
          value: item.key ? `${item.id}_${item.key}` : item.id.toString(),
          attrs: { disabled: false },
          data: item,
        }
      })
    },
    isStripeSelected(): boolean {
      if (this.selectedMethod) {
        return this.selectedMethod.type === PaymentProcessorsEnum.Stripe
      }

      return false
    },
  },
  actions: {
    setStripeInstance(instance: any) {
      this.stripe.instance = instance
    },
    setStripeCard(card: any) {
      this.stripe.card = card
    },
    setSelected(type: string | null) {
      if (this.paymentMethods.length && type) {
        const index = type.indexOf('_')
        let processorKey: string | null = null
        if (index !== -1) {
          processorKey = type.substring(type.indexOf('_') + 1)
        }
        const processorId = type.split('_', 1)[0]
        this.selectedMethod =
          this.paymentMethods.find((method: FrontendPaymentProcessor) => {
            return method.id === parseInt(processorId) && (processorKey ? method.key === processorKey : true)
          }) ?? null
      } else {
        this.selectedMethod = null
      }
    },
    setStripeToken(value: string | null) {
      this.stripe.token = value
    },
    async setStripeEnabled() {
      this.stripeEnabled = await loadStripe()
    },
    setCountries(countries: CountryItem[]) {
      this.countries = countries
    },
    setPaymentMethods(paymentMethods: FrontendPaymentProcessor[]) {
      this.paymentMethods = paymentMethods
    },
    async updatePaymentMethods(countries: DestinationInterface[], amount: string | number, country_id: number) {
      const methods = await getPaymentMethods(countries, parseFloat(amount as string) ?? 0, country_id)
      this.setPaymentMethods(methods)
    },
    setErrorMessage(errorMessage: string) {
      this.errorMessage = errorMessage

      if (errorMessage) {
        alert(errorMessage)
      }
    },
    async fetch() {
      const { t } = useLang()

      const rawCountries = await getAvailableCountries()

      const countriesArray: CountryItem[] = []
      rawCountries.forEach((rawCountry: DestinationInterface) => {
        countriesArray.push({
          value: rawCountry.country_id.toString(),
          label: t(rawCountry.country.name),
        })
      })
      this.setCountries(countriesArray)
      await this.setStripeEnabled()
    },
    formatPrice(price: string | number) {
      const storeConfig = useStoreConfig()

      return storeConfig.getSelectedCurrency?.formatting.replaceAll('{amount}', `${parseFloat(price.toString())}`)
    },
    formatFee(data: { fee: number; fee_type: number }) {
      let value = ''
      switch (data.fee_type) {
        case PaymentFeeTypes.Fixed:
          value = `+${toPrice(data.fee)}`
          break
        case PaymentFeeTypes.Percentage:
          value = `+${data.fee} %`
          break
      }

      return value
    },
    async deposit(formData: any) {
      if (this.isStripeSelected) {
        this.stripe.instance.createPaymentMethod('card', this.stripe.card).then((result: any) => {
          if (result.error) {
            this.setErrorMessage(result.error.message)
          } else {
            this.executePayment(formData, String(result.paymentMethod.id))
          }
        })
      } else {
        await this.executePayment(formData, null)
      }
    },
    async executePayment(
      formData: { amount: number; country_id: number; provider_type: number },
      stripeToken: string | null
    ) {
      const identity = useIdentity()
      const user = await identity.getUser()

      if (!user) {
        this.setErrorMessage('balance.user_unauthenticated')

        return
      }

      const countries: DestinationInterface[] = await $fetch('/api/auth/destinations', fetchRetryParameters)

      const data = {
        countries,
        paymentProcessorId: this.selectedMethod ? this.selectedMethod.id : null,
        paymentMethodKey: this.selectedMethod ? this.selectedMethod.key : null,
        stripeToken,
        depositAmount: formData.amount,
        sumToPay: parseFloat(this.selectedMethod?.sum_to_pay as string),
        countryId: formData.country_id,
      }

      utilsExecutePayment(data).then((response: any) => {
        if (typeof response === 'object') {
          if (Object.hasOwn(response, 'action') && Object.hasOwn(response, 'details')) {
            switch (response.action) {
              case 'show_error':
                if (typeof response.details === 'string') {
                  this.setErrorMessage(response.details)
                }
                break
              case 'redirect':
                if (typeof response.details === 'string') {
                  window.location.href = response.details
                }
                break
              case PaymentActionResponseEnum.StripeRequiresAuthentication:
                if (typeof response.details === 'object' && response.details) {
                  this.stripeConfirmIntent(response.details.order_id, response.details.payment_intent_client_secret)
                }
                break
            }
          }
        }
      })
    },
    stripeConfirmIntent(orderId: string, secret: string) {
      this.stripe.instance.handleCardAction(secret).then((result: PaymentCardActionResponse) => {
        if (result.error) {
          nextTick().then(() => this.setErrorMessage(result.error.message))
        } else {
          confirmPaymentIntent(orderId, result.paymentIntent.id).then((res: PaymentIntentResponse) => {
            window.location.href = res.redirect
          })
        }
      })
    },
  },
})
